import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import { InstallAppButton } from "@/components/InstallAppButton";

const MEMBER_LINKS = [
  { to: "/about", label: "About" },
  { to: "/devotionals", label: "Devotionals" },
  { to: "/events", label: "Events" },
  { to: "/onair", label: "On the Air" },
  { to: "/directory", label: "Members" },
  { to: "/forum", label: "Community" },
  { to: "/prayers", label: "Prayer" },
  { to: "/assistant", label: "Barnabas AI" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onLanding = location.pathname === "/";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled || !onLanding
          ? "bg-bone/80 backdrop-blur-xl border-b border-charcoal/10 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 group">
          <img src="/logo.png" alt="In His Image Movement Ministries" className="h-20 md:h-24 w-auto object-contain drop-shadow-sm" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {MEMBER_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-sans text-sm text-black/75 hover:text-black transition-colors duration-300"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/account"
                data-testid="nav-account"
                className="hidden md:inline-block font-mono text-xs uppercase tracking-widest text-black/70 hover:text-black transition-colors"
              >
                Account
              </Link>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className="hidden md:inline-block font-mono text-xs uppercase tracking-widest px-5 py-2.5 bg-charcoal text-bone hover:bg-navy transition-colors duration-300"
              >
                Dashboard
              </Link>
              <button
                onClick={() => { logout(); navigate("/"); }}
                data-testid="nav-logout"
                className="hidden md:inline-block font-mono text-xs uppercase tracking-widest text-black/60 hover:text-rust transition-colors"
              >
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden md:inline-block font-mono text-xs uppercase tracking-widest text-black/75 hover:text-black transition-colors">
                Sign In
              </Link>
              <Link to="/register" data-testid="nav-join" className="font-mono text-xs uppercase tracking-widest px-5 py-2.5 bg-charcoal text-bone hover:bg-olive transition-colors duration-300">
                Join
              </Link>
            </>
          )}
          <button className="lg:hidden text-charcoal" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-bone border-t border-charcoal/10 mt-4 px-6 py-6 flex flex-col gap-4">
          {MEMBER_LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-serif text-2xl">{l.label}</Link>
          ))}
          {user ? (
            <Link to="/dashboard" onClick={() => setOpen(false)} className="font-serif text-2xl">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="font-serif text-2xl">Sign In</Link>
              <div onClick={() => setOpen(false)}>
                <InstallAppButton label="Install App" className="font-serif text-2xl flex items-center gap-3 text-navy" />
              </div>
            </>
          )}
        </div>
      )}
    </header>
  );
}
